import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Video, Upload, Square, RotateCcw, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VIDEO_DEFAULT_SECONDS, VIDEO_DURATION_OPTIONS, VIDEO_MAX_MB } from './types';

export interface RecordedVideo {
  blob: Blob;
  fileName: string;
  previewUrl: string;
  /** Duração lida no navegador; null quando o container não informa (webm do MediaRecorder). */
  durationSec: number | null;
  /** Duração escolhida pelo cliente — o backend corta o vídeo nela. */
  limitSec: number;
}

interface VideoRecorderProps {
  value: RecordedVideo | null;
  onChange: (video: RecordedVideo | null) => void;
  disabled?: boolean;
}

type Phase = 'idle' | 'countdown' | 'recording';

// Safari grava MP4; Chrome/Firefox, WebM. O servidor normaliza tudo para MP4 com ffmpeg.
const MIME_CANDIDATES = [
  'video/mp4;codecs=avc1',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return '';
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || '';
}

function canRecord(): boolean {
  return typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;
}

function readDuration(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => resolve(Number.isFinite(el.duration) ? el.duration : null);
    el.onerror = () => resolve(null);
    el.src = url;
  });
}

const VideoRecorder: React.FC<VideoRecorderProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [countdown, setCountdown] = useState(3);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState('');
  const [limitSec, setLimitSec] = useState(value?.limitSec ?? VIDEO_DEFAULT_SECONDS);
  const limitRef = useRef(limitSec);
  limitRef.current = limitSec;

  const liveRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timersRef = useRef<number[]>([]);
  const startedAtRef = useRef(0);
  const valueRef = useRef(value);
  valueRef.current = value;

  const clearTimers = () => {
    timersRef.current.forEach((id) => window.clearInterval(id));
    timersRef.current = [];
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const setVideo = useCallback((next: RecordedVideo | null) => {
    if (valueRef.current?.previewUrl) URL.revokeObjectURL(valueRef.current.previewUrl);
    onChange(next);
  }, [onChange]);

  useEffect(() => () => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    stopStream();
    if (valueRef.current?.previewUrl) URL.revokeObjectURL(valueRef.current.previewUrl);
  }, []);

  // Liga a câmera ao <video> ao vivo depois do commit do render que o monta (com setTimeout o elemento
  // ainda podia não existir e a prévia ficava preta durante a gravação)
  useEffect(() => {
    const el = liveRef.current;
    const stream = streamRef.current;
    if (phase === 'idle' || !el || !stream || el.srcObject === stream) return;
    el.srcObject = stream;
    el.play().catch(() => {});
  }, [phase]);

  const stopRecording = useCallback(() => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
  }, []);

  // Descarta a gravação em andamento (botão X / Esc) sem gerar vídeo
  const cancelRecording = useCallback(() => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    stopStream();
    setPhase('idle');
  }, []);

  // Câmera em tela cheia enquanto grava: a camada fixa cobre a viewport em qualquer aparelho e, onde o
  // navegador permite (desktop/Android), também entra na Fullscreen API. iPhone só tem fullscreen
  // nativo para <video> (esconderia contagem e botões), então lá fica só a camada.
  const overlayOpen = phase !== 'idle';
  useEffect(() => {
    if (!overlayOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const el = overlayRef.current;
    if (el?.requestFullscreen && !document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelRecording(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, [overlayOpen, cancelRecording]);

  const beginRecording = (stream: MediaStream) => {
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 2_000_000,
      });
    } catch (_) {
      stopStream();
      setPhase('idle');
      setError(t('testimonialsUi.recordUnsupported'));
      return;
    }
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const limit = limitRef.current;
      const elapsed = Math.min(limit, (Date.now() - startedAtRef.current) / 1000);
      const type = (recorder.mimeType || mimeType || 'video/webm').split(';')[0];
      const blob = new Blob(chunksRef.current, { type });
      stopStream();
      setPhase('idle');
      if (blob.size === 0) {
        setError(t('testimonialsUi.recordUnsupported'));
        return;
      }
      setVideo({
        blob,
        fileName: `depoimento.${type.includes('mp4') ? 'mp4' : 'webm'}`,
        previewUrl: URL.createObjectURL(blob),
        durationSec: Math.round(elapsed * 10) / 10,
        limitSec: limit,
      });
    };
    recorderRef.current = recorder;
    recorder.start(250);
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setPhase('recording');
    const tick = window.setInterval(() => {
      const ms = Date.now() - startedAtRef.current;
      setElapsedMs(ms);
      if (ms >= limitRef.current * 1000) stopRecording();
    }, 100);
    timersRef.current.push(tick);
  };

  const startRecording = async () => {
    setError('');
    if (!canRecord()) {
      setError(t('testimonialsUi.recordUnsupported'));
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
    } catch (_) {
      setError(t('testimonialsUi.cameraDenied'));
      return;
    }
    setVideo(null);
    streamRef.current = stream;
    setCountdown(3);
    setPhase('countdown');
    let remaining = 3;
    const id = window.setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setCountdown(remaining);
        return;
      }
      window.clearInterval(id);
      timersRef.current = timersRef.current.filter((x) => x !== id);
      beginRecording(stream);
    }, 1000);
    timersRef.current.push(id);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    if (!file.type.startsWith('video/')) {
      setError(t('testimonialsUi.fileNotVideo'));
      return;
    }
    if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
      setError(t('testimonialsUi.fileTooBig', { mb: VIDEO_MAX_MB }));
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    const duration = await readDuration(previewUrl);
    setVideo({
      blob: file,
      fileName: file.name || 'depoimento.mp4',
      previewUrl,
      durationSec: duration != null ? Math.round(duration * 10) / 10 : null,
      limitSec: limitRef.current,
    });
  };

  // Trocar a duração depois de escolher um arquivo só muda onde o backend corta
  const chooseLimit = (seconds: number) => {
    setLimitSec(seconds);
    if (valueRef.current) onChange({ ...valueRef.current, limitSec: seconds });
  };

  const progress = Math.min(1, elapsedMs / (limitSec * 1000));
  const remainingSec = Math.max(0, Math.ceil(limitSec - elapsedMs / 1000));
  const durationLabel = (seconds: number) =>
    seconds >= 60 ? t('testimonialsUi.durationMinute') : t('testimonialsUi.durationSeconds', { count: seconds });
  const busy = phase !== 'idle';

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="mb-3">
        <p className="text-sm font-medium text-gray-700">{t('testimonialsUi.videoSection')}</p>
        <p className="text-xs text-gray-500">{t('testimonialsUi.videoHint')}</p>
      </div>

      {!busy && (
        <div className="mb-3">
          <span id="video-duration-label" className="block text-xs font-medium text-gray-600 mb-1.5">{t('testimonialsUi.durationLabel')}</span>
          <div role="radiogroup" aria-labelledby="video-duration-label" className="flex flex-wrap gap-2">
            {VIDEO_DURATION_OPTIONS.map((seconds) => (
              <button
                key={seconds}
                type="button"
                role="radio"
                aria-checked={limitSec === seconds}
                onClick={() => chooseLimit(seconds)}
                disabled={disabled}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium tabular-nums transition-colors disabled:opacity-50 ${
                  limitSec === seconds
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-primary-400'
                }`}
              >
                {durationLabel(seconds)}
              </button>
            ))}
          </div>
        </div>
      )}

      {busy && createPortal(
        <div
          ref={overlayRef}
          className="fixed inset-0 z-[60] bg-black text-white"
          role="dialog"
          aria-modal="true"
          aria-label={t('testimonialsUi.videoSection')}
        >
          <video ref={liveRef} muted playsInline autoPlay className="absolute inset-0 h-full w-full object-cover -scale-x-100" />

          {phase === 'recording' && (
            <div className="absolute inset-x-0 top-0 h-1.5 bg-white/25">
              <div className="h-full bg-red-500 transition-[width] duration-100 ease-linear" style={{ width: `${progress * 100}%` }} />
            </div>
          )}

          <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 px-4 pt-[max(1.25rem,env(safe-area-inset-top))]">
            {phase === 'recording' ? (
              <div className="flex items-center gap-2 rounded-full bg-black/60 px-3.5 py-1.5 text-sm font-medium">
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
                {t('testimonialsUi.recording')} · <span className="tabular-nums">{remainingSec}s</span>
              </div>
            ) : <span />}
            <button
              type="button"
              onClick={cancelRecording}
              aria-label={t('testimonialsUi.cancelRecording')}
              title={t('testimonialsUi.cancelRecording')}
              className="rounded-full bg-black/60 p-2.5 hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {phase === 'countdown' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
              <span className="text-base">{t('testimonialsUi.getReady')}</span>
              <span className="text-8xl font-bold tabular-nums" aria-live="assertive">{countdown}</span>
            </div>
          )}

          {phase === 'recording' && (
            <div className="absolute inset-x-0 bottom-0 flex justify-center pb-[max(2rem,env(safe-area-inset-bottom))]">
              <button
                type="button"
                onClick={stopRecording}
                className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-gray-900 shadow-xl hover:bg-gray-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
              >
                <Square className="h-5 w-5 fill-current text-red-600" />
                {t('testimonialsUi.stop')}
              </button>
            </div>
          )}
        </div>,
        document.body
      )}

      {!busy && value && (
        <div>
          <video src={value.previewUrl} controls playsInline className="w-full rounded-lg bg-black aspect-video" />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-green-700">
              {t('testimonialsUi.videoReady', { duration: Math.min(limitSec, value.durationSec ?? limitSec) })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={startRecording}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                {t('testimonialsUi.retake')}
              </button>
              <button
                type="button"
                onClick={() => setVideo(null)}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {t('testimonialsUi.remove')}
              </button>
            </div>
          </div>
          {value.durationSec != null && value.durationSec > limitSec + 0.5 && (
            <p className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
              {t('testimonialsUi.willTrim', { duration: Math.round(value.durationSec), seconds: limitSec })}
            </p>
          )}
        </div>
      )}

      {!busy && !value && (
        <div className="flex flex-col gap-2 sm:flex-row">
          {canRecord() && (
            <button
              type="button"
              onClick={startRecording}
              disabled={disabled}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <Video className="h-4 w-4" />
              {t('testimonialsUi.record')}
            </button>
          )}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {t('testimonialsUi.upload')}
          </button>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFile} />

      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export default VideoRecorder;
